<template>
  <form
    role="search"
    aria-label="Search"
    class="search-form"
    @submit.prevent
  >
    <v-menu
      v-model="isOpen"
      :content-class="dropdownClass"
      offset-y
      :close-on-content-click="false"
      bottom
      allow-overflow
      eager
      nudge-bottom="6"
      max-width="560"
      min-width="400"
    >
      <template v-slot:activator="{ attrs }">
        <v-text-field
          ref="searchInput"
          v-model="inputValue"
          v-bind="attrs"
          autocomplete="off"
          solo
          flat
          hide-details
          :placeholder="placeholderText"
          :loading="isLoading || !haveCurrentUser"
          :disabled="!haveCurrentUser"
          class="search-input"
          @focus="handleFocus"
          @blur="handleBlur"
          @click.native="openMenu"
          @input="handleInput"
          @keydown.escape="handleEscape"
          @keydown.down.prevent="handleArrowDown"
          @keydown.up.prevent="handleArrowUp"
          @keydown.enter.prevent="handleEnter"
          @keydown.tab="handleTab"
        >
          <template v-slot:prepend-inner>
            <font-awesome-icon
              :icon="['fas', 'magnifying-glass']"
              class="search-icon"
            />
          </template>
          <template v-slot:append>
            <v-btn
              v-if="searchText"
              icon
              small
              @click.stop="clearSearch"
            >
              <font-awesome-icon
                :icon="['fas', 'times']"
                size="sm"
              />
            </v-btn>
          </template>
        </v-text-field>
      </template>

      <!-- Dropdown Content -->
      <v-card class="search-dropdown">
        <!-- Authentication Waiting State -->
        <div
          v-if="!haveCurrentUser"
          class="pa-4 text-center"
        >
          <v-progress-circular
            indeterminate
            size="24"
            color="primary"
          />
          <div class="mt-2 grey--text text--darken-1">
            Initializing search...
          </div>
          <div class="caption grey--text mt-1">
            Please sign in to your account
          </div>
        </div>

        <!-- Loading State -->
        <div
          v-else-if="isLoading && !hasResults"
          class="pa-4 text-center"
        >
          <v-progress-circular
            indeterminate
            size="24"
            color="primary"
          />
          <div class="mt-2 grey--text text--darken-1">
            Loading search options...
          </div>
        </div>

        <!-- Error State -->
        <div
          v-else-if="hasError"
          class="pa-4 text-center"
        >
          <font-awesome-icon
            :icon="isPermissionError ? ['fas', 'lock'] : ['fas', 'exclamation-circle']"
            class="error--text mb-2"
            size="lg"
          />
          <div class="error--text">
            {{ isPermissionError ? 'Access denied to search' : 'Failed to load search options' }}
          </div>
          <div
            v-if="isPermissionError"
            class="caption grey--text mt-1"
          >
            Your account may not have search permissions.
            Contact your administrator if this persists.
          </div>
          <v-btn
            text
            small
            color="primary"
            class="mt-2"
            @click="retryLoad"
          >
            Try Again
          </v-btn>
        </div>

        <!-- Empty Search State (no query) -->
        <div
          v-else-if="!searchText && !hasResults"
          class="search-empty-state"
        >
          <!-- Current Page -->
          <template v-if="currentPageOption">
            <div class="search-section-header">
              <span>CURRENT PAGE</span>
            </div>
            <v-list
              dense
              class="py-0"
            >
              <v-list-item class="search-result-item current-page-item">
                <v-list-item-icon class="mr-3">
                  <font-awesome-icon
                    :icon="['fas', 'location-dot']"
                    class="primary--text"
                    size="sm"
                  />
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="subtitle-2 font-weight-bold primary--text">
                    {{ currentPageOption.text }}
                  </v-list-item-title>
                  <v-list-item-subtitle
                    v-if="currentPageOption.description"
                    class="caption"
                  >
                    {{ currentPageOption.description }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-divider class="my-2" />
          </template>

          <!-- Recent Searches -->
          <template v-if="recentSearches.length > 0">
            <div class="search-section-header">
              <span>RECENT</span>
              <v-btn
                x-small
                text
                color="grey"
                @click="clearRecent"
              >
                Clear
              </v-btn>
            </div>
            <v-list
              dense
              class="py-0"
            >
              <v-list-item
                v-for="recent in recentSearches"
                :key="recent.docId || recent.href"
                class="search-result-item"
                @click="selectRecent(recent)"
              >
                <v-list-item-icon class="mr-3">
                  <font-awesome-icon
                    :icon="['fas', 'clock-rotate-left']"
                    class="grey--text"
                    size="sm"
                  />
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="subtitle-2">
                    {{ recent.text }}
                  </v-list-item-title>
                  <v-list-item-subtitle
                    v-if="recent.description"
                    class="caption"
                  >
                    {{ recent.description }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-divider class="my-2" />
          </template>

          <v-alert
            v-if="showSearchPrimer"
            outlined
            dense
            type="info"
            class="mx-3 mb-2 search-primer-alert"
          >
            <div class="d-flex flex-column">
              <span class="font-weight-medium">Search Primer:</span>
              <span>
                Search by network, team, person, or project. Selecting a result updates your
                current context and can change downstream report content.
              </span>
            </div>
            <div class="mt-2 d-flex align-center justify-end">
              <v-btn
                text
                small
                class="text-none"
                @click="openSearchGuide"
              >
                Learn More
              </v-btn>
              <v-btn
                icon
                small
                aria-label="Hide search help"
                @click="dismissSearchPrimer"
              >
                <font-awesome-icon
                  :icon="['fas', 'xmark']"
                  size="sm"
                />
              </v-btn>
            </div>
          </v-alert>

          <!-- Quick Actions -->
          <div class="search-section-header">
            <span>QUICK ACTIONS</span>
          </div>
          <v-list
            dense
            class="py-0"
          >
            <v-list-item
              v-for="action in quickActions"
              :key="action.href"
              class="search-result-item"
              @click="selectQuickAction(action)"
            >
              <v-list-item-icon class="mr-3">
                <font-awesome-icon
                  :icon="action.icon"
                  class="primary--text"
                  size="sm"
                />
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="subtitle-2">
                  {{ action.text }}
                </v-list-item-title>
                <v-list-item-subtitle class="caption">
                  {{ action.description }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <!-- Keyboard Hint -->
          <div class="search-hint pa-2 text-center grey--text caption">
            <kbd>/</kbd> to focus search
            <v-btn
              v-if="searchPrimerDismissed"
              text
              x-small
              class="ml-2 text-none"
              @click.stop="restoreSearchPrimer"
            >
              Show help
            </v-btn>
          </div>
        </div>

        <!-- Search Results -->
        <div
          v-else-if="hasResults"
          class="search-results"
        >
          <!-- Current Page indicator (above results when no query typed) -->
          <template v-if="!searchText && currentPageOption">
            <div class="search-section-header">
              <span>CURRENT PAGE</span>
            </div>
            <v-list
              dense
              class="py-0"
            >
              <v-list-item class="search-result-item current-page-item">
                <v-list-item-icon class="mr-3">
                  <font-awesome-icon
                    :icon="['fas', 'location-dot']"
                    class="primary--text"
                    size="sm"
                  />
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="subtitle-2 font-weight-bold primary--text">
                    {{ currentPageOption.text }}
                  </v-list-item-title>
                  <v-list-item-subtitle
                    v-if="currentPageOption.description"
                    class="caption"
                  >
                    {{ currentPageOption.description }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-divider class="my-2" />
          </template>

          <div
            v-for="category in visibleCategories"
            :key="category.key"
            class="search-category"
          >
            <!-- Category Header -->
            <div class="search-section-header">
              <span>
                {{ category.label }}
                <span class="grey--text ml-1">({{ category.totalCount }})</span>
              </span>
              <v-btn
                v-if="category.totalCount > category.items.length"
                x-small
                text
                color="primary"
                @click="showMoreResults(category.key)"
              >
                View All
              </v-btn>
            </div>

            <!-- Category Results -->
            <v-list
              dense
              class="py-0"
            >
              <v-list-item
                v-for="(item, index) in category.items"
                :key="`${category.key}-${index}`"
                :class="{
                  'search-result-item': true,
                  'search-result-highlighted': isHighlighted(category.key, index)
                }"
                @click="selectResult(item)"
                @mouseenter="setHighlight(category.key, index)"
              >
                <v-list-item-icon class="mr-3">
                  <font-awesome-icon
                    :icon="category.icon"
                    class="grey--text text--darken-1"
                    size="sm"
                  />
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="subtitle-2">
                    <!-- Highlighted text with matched chunks -->
                    <template v-if="item.chunks && item.chunks.length > 0">
                      <span
                        v-for="(chunk, ci) in item.chunks"
                        :key="ci"
                        :class="{ 'font-weight-bold primary--text': chunk.isMatched }"
                      >{{ chunk.value }}</span>
                    </template>
                    <template v-else>
                      {{ item.text }}
                    </template>
                  </v-list-item-title>
                  <v-list-item-subtitle
                    v-if="item.description"
                    class="caption"
                  >
                    {{ item.description }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action
                  v-if="item.reportKey"
                  class="ml-2"
                >
                  <v-chip
                    x-small
                    :color="reportKeyColor(item.reportKey)"
                    :text-color="reportKeyTextColor(item.reportKey)"
                    class="caption font-weight-medium"
                  >
                    {{ formatReportKey(item.reportKey) }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </div>

        <!-- No Results -->
        <div
          v-else-if="searchText && !hasResults && !isLoading"
          class="pa-4 text-center"
        >
          <font-awesome-icon
            :icon="['fas', 'search']"
            class="grey--text mb-2"
            size="lg"
          />
          <div class="grey--text text--darken-1">
            No results for "{{ searchText }}"
          </div>
          <div class="caption grey--text mt-1">
            Try different keywords or check your spelling
          </div>
        </div>
      </v-card>
    </v-menu>
  </form>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import searchService from '@/services/searchService'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'SearchBarFirestore',

  data: () => ({
    searchText: '',
    isOpen: false,
    isLoading: false,
    hasError: false,
    isPermissionError: false,
    searchResults: null,
    highlightedCategory: null,
    highlightedIndex: -1,
    debounceTimer: null,
    retryTimer: null,
    retryCount: 0,
    isSearchFocused: false,
    searchPrimerDismissed: false,
    localRecentSearches: searchService.getRecentSearches(),
  }),

  computed: {
    ...mapAuthGetters(['isInternalUser', 'haveCurrentUser', 'userEmail', 'currentUser']),
    ...mapToolsState({
      selectedSearch: (state) => state.selectedSearch,
    }),

    displayText() {
      const ss = this.selectedSearch
      if (!ss || !ss.text) return ''
      return ss.text
    },

    inputValue: {
      get() {
        if (this.isSearchFocused) return this.searchText
        return this.displayText
      },
      set(value) {
        this.searchText = value
      },
    },

    currentPageOption() {
      const ss = this.selectedSearch
      if (!ss || !ss.text) return null
      return {
        text: ss.text,
        description: ss.reportKey ? this.formatReportKey(ss.reportKey) : '',
        href: this.$route?.fullPath,
        reportKey: ss.reportKey,
        reportId: ss.reportId,
      }
    },

    placeholderText() {
      if (!this.haveCurrentUser) {
        return 'Signing in...'
      }

      if (this.isPermissionError) {
        return 'Search unavailable — access denied'
      }

      return 'Search networks, people, projects...'
    },

    dropdownClass() {
      return 'search-dropdown-container white'
    },

    recentSearches() {
      return this.localRecentSearches
    },

    quickActions() {
      return searchService.getQuickActions({ isInternalUser: this.isInternalUser })
    },

    hasResults() {
      return this.searchResults && !this.searchResults.isEmpty
    },

    visibleCategories() {
      if (!this.searchResults || !this.searchResults.categories) return []

      return Object.entries(this.searchResults.categories)
        .filter(([, cat]) => cat.items.length > 0)
        .map(([key, cat]) => ({ key, ...cat }))
        .sort((a, b) => a.sortOrder - b.sortOrder)
    },

    flatResults() {
      // Flatten results for keyboard navigation
      const flat = []
      this.visibleCategories.forEach((category) => {
        category.items.forEach((item, index) => {
          flat.push({
            category: category.key,
            index,
            item,
          })
        })
      })
      return flat
    },

    showSearchPrimer() {
      return !this.searchPrimerDismissed && !this.searchText && this.haveCurrentUser
    },

    searchPrimerStorageKey() {
      const email = this.userEmail || 'anonymous'
      return `iadops.search.primer.dismissed:${email}`
    },
  },

  watch: {
    isOpen(newValue) {
      if (newValue && !searchService.isInitialized) {
        this.initializeSearch()
      }
    },

    // Initialize search when user authenticates
    haveCurrentUser(newValue) {
      if (newValue && !searchService.isInitialized && !searchService.isLoading) {
        this.initializeSearch()
      }
    },

    // Resolve selectedSearch when route changes
    $route: {
      handler() {
        if (searchService.isInitialized) {
          this.resolveSelectedSearch()
        }
      },
      immediate: true,
    },
  },

  created() {
    this.restoreSearchPrimerState()

    // Set up global keyboard shortcut
    document.addEventListener('keydown', this.handleGlobalKeydown)

    // Cross-tab sync for recent searches
    this.storageListener = (event) => {
      if (event.key === 'IADOPS_RECENT_SEARCHES') {
        this.localRecentSearches = searchService.getRecentSearches()
      }
    }
    window.addEventListener('storage', this.storageListener)

    // Only pre-initialize search service if user is authenticated
    // Otherwise, wait for haveCurrentUser watcher to trigger initialization
    if (this.haveCurrentUser) {
      this.initializeSearch()
    }
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleGlobalKeydown)
    window.removeEventListener('storage', this.storageListener)
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
    }
  },

  methods: {
    restoreSearchPrimerState() {
      this.searchPrimerDismissed = localStorage.getItem(this.searchPrimerStorageKey) === 'true'
    },

    dismissSearchPrimer() {
      this.searchPrimerDismissed = true
      localStorage.setItem(this.searchPrimerStorageKey, 'true')
    },

    restoreSearchPrimer() {
      this.searchPrimerDismissed = false
      localStorage.setItem(this.searchPrimerStorageKey, 'false')
    },

    openSearchGuide() {
      this.closeAndNavigate('/docs/guides#search')
    },

    async initializeSearch() {
      if (searchService.isInitialized && !searchService.isLoading) return

      // Scope cache to current user and set team access for filtering
      if (this.userEmail) {
        const network = this.currentUser?.network || []
        const teamCodes = []
        const billingCodes = []

        network.forEach((entry) => {
          const orgCode = entry?.organization?.code
          const teams = Array.isArray(entry?.team) ? entry.team : []

          if (orgCode && !billingCodes.includes(orgCode)) billingCodes.push(orgCode)

          teams.forEach((t) => {
            if (orgCode && t?.code) teamCodes.push(`${orgCode}/${t.code}`)
          })
        })

        searchService.setUser(this.userEmail, {
          billingCodes,
          teamCodes,
          isInternal: this.isInternalUser,
        })
      }

      // eslint-disable-next-line no-console
      console.log('[SearchBar] Initializing search service...')

      this.hasError = false
      this.isPermissionError = false

      // Don't show loading spinner if cached data is already available
      // (stale-while-revalidate will refresh in background)
      if (!searchService.isInitialized) {
        this.isLoading = true
      }

      try {
        const success = await searchService.initialize()

        if (!success && searchService.isPermissionError) {
          this.isPermissionError = true
          this.hasError = true
          this.scheduleRetry()
        } else if (!success) {
          this.hasError = true
        }
      } catch (error) {
        this.hasError = true
        // eslint-disable-next-line no-console
        console.error('[SearchBar] Failed to initialize search:', error)
      } finally {
        this.isLoading = false
        // Resolve selectedSearch now that the index is ready
        if (searchService.isInitialized) {
          this.resolveSelectedSearch()
        }
        // If the dropdown is already open, show results immediately
        if (this.isOpen && searchService.isInitialized && !this.searchText) {
          this.showAllResults()
        }
      }
    },

    scheduleRetry() {
      // Retry up to 2 times with increasing delay (3s, 6s)
      if (this.retryCount >= 2) return

      this.retryCount += 1
      const delay = this.retryCount * 3000

      // eslint-disable-next-line no-console
      console.log(`[SearchBar] Scheduling retry ${this.retryCount}/2 in ${delay}ms`)

      this.retryTimer = setTimeout(() => {
        searchService.reset()
        this.initializeSearch()
      }, delay)
    },

    async retryLoad() {
      this.retryCount = 0
      if (this.retryTimer) {
        clearTimeout(this.retryTimer)
        this.retryTimer = null
      }
      searchService.reset()
      await this.initializeSearch()
    },

    showAllResults() {
      // Display all indexed results when search is empty
      if (!searchService.isInitialized) return

      this.searchResults = searchService.searchAll()

      // Reset highlight to first result if available
      if (this.hasResults && this.visibleCategories.length > 0) {
        this.highlightedCategory = this.visibleCategories[0].key
        this.highlightedIndex = 0
      } else {
        this.resetHighlight()
      }
    },

    performSearch(query) {
      if (!searchService.isInitialized) {
        this.searchResults = null
        return
      }

      if (!query || query.trim().length === 0) {
        // Show all results when query is empty (for testing/convenience)
        this.showAllResults()
        return
      }

      this.searchResults = searchService.search(query)

      // Reset highlight to first result if available
      if (this.hasResults && this.visibleCategories.length > 0) {
        this.highlightedCategory = this.visibleCategories[0].key
        this.highlightedIndex = 0
      } else {
        this.resetHighlight()
      }
    },

    handleFocus() {
      this.isSearchFocused = true
      this.searchText = ''
      this.openMenu()
    },

    handleBlur() {
      this.isSearchFocused = false
    },

    handleTab() {
      this.isOpen = false
    },

    openMenu() {
      if (!this.haveCurrentUser || this.isOpen) return

      this.isOpen = true

      if (!searchService.isInitialized && !searchService.isLoading) {
        this.initializeSearch()
      } else if (searchService.isInitialized && !this.searchText) {
        this.showAllResults()
      }
    },

    handleInput(value) {
      // Debounce search for performance
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }

      this.debounceTimer = setTimeout(() => {
        this.performSearch(value)
      }, 150)
    },

    handleEscape() {
      if (this.searchText) {
        this.clearSearch()
      } else {
        this.isOpen = false
        this.$refs.searchInput.blur()
      }
    },

    handleArrowDown() {
      if (!this.hasResults) return

      const currentFlatIndex = this.getCurrentFlatIndex()
      const nextIndex = Math.min(currentFlatIndex + 1, this.flatResults.length - 1)
      this.setHighlightByFlatIndex(nextIndex)
    },

    handleArrowUp() {
      if (!this.hasResults) return

      const currentFlatIndex = this.getCurrentFlatIndex()
      const prevIndex = Math.max(currentFlatIndex - 1, 0)
      this.setHighlightByFlatIndex(prevIndex)
    },

    handleEnter() {
      if (!this.hasResults || this.highlightedCategory === null) return

      const flatIndex = this.getCurrentFlatIndex()
      if (flatIndex >= 0 && flatIndex < this.flatResults.length) {
        this.selectResult(this.flatResults[flatIndex].item)
      }
    },

    handleGlobalKeydown(event) {
      // Focus search on "/" key (when not in input)
      if (event.key === '/' && !this.isInputFocused()) {
        event.preventDefault()
        this.$refs.searchInput.focus()
        this.isOpen = true
      }
    },

    isInputFocused() {
      const { activeElement } = document
      return activeElement && (
        activeElement.tagName === 'INPUT'
        || activeElement.tagName === 'TEXTAREA'
        || activeElement.isContentEditable
      )
    },

    clearSearch() {
      this.searchText = ''
      this.searchResults = null
      this.resetHighlight()
    },

    isHighlighted(category, index) {
      return this.highlightedCategory === category && this.highlightedIndex === index
    },

    setHighlight(category, index) {
      this.highlightedCategory = category
      this.highlightedIndex = index
    },

    resetHighlight() {
      this.highlightedCategory = null
      this.highlightedIndex = -1
    },

    getCurrentFlatIndex() {
      return this.flatResults.findIndex(
        (r) => r.category === this.highlightedCategory && r.index === this.highlightedIndex,
      )
    },

    setHighlightByFlatIndex(flatIndex) {
      if (flatIndex >= 0 && flatIndex < this.flatResults.length) {
        const { category, index } = this.flatResults[flatIndex]
        this.setHighlight(category, index)
      }
    },

    showMoreResults(categoryKey) {
      // Expand results for this category
      // For now, navigate to the relevant landing page
      const categoryRoutes = {
        network: '/reports/pacing',
        team: '/reports/pacing',
        person: '/internal/people',
        project: '/internal/projects',
        dashboard: '/dashboard',
        doc: '/docs',
        admin: '/admin',
        setting: '/settings',
      }

      const route = categoryRoutes[categoryKey] || '/dashboard'
      this.closeAndNavigate(route)
    },

    selectResult(item) {
      if (!item) return

      // Add to recent searches
      searchService.addToRecent(item)
      this.localRecentSearches = searchService.getRecentSearches()

      // Navigate to the item's href
      const href = item.href || this.buildHref(item)
      this.closeAndNavigate(href)

      // Emit selection event for parent components
      this.$emit('select', item)
    },

    selectRecent(recent) {
      if (recent.href) {
        this.closeAndNavigate(recent.href)
      }
    },

    selectQuickAction(action) {
      if (action.href) {
        this.closeAndNavigate(action.href)
      }
    },

    buildHref(item) {
      const { reportKey, reportId, subId, route, billingCode, teamId, tabId } = item

      // Pacing/Discrepancy use query params for billingCode and teamId
      const isPacingOrDiscrepancy = ['pacing', 'discrepancy'].includes(reportKey)
      if (isPacingOrDiscrepancy) {
        const params = new URLSearchParams()
        if (billingCode || reportId) params.set('billingCode', billingCode || reportId)
        if (teamId || subId) params.set('teamId', teamId || subId)
        if (tabId) params.set('tab', tabId)
        const qs = params.toString()
        return `/reports/${reportKey}${qs ? `?${qs}` : ''}`
      }

      // Default: path-based href
      let href = `/${route || 'dashboard'}`

      if (reportKey) {
        href += `/${reportKey}`
      }

      if (reportId) {
        href += `/${reportId}`
      }

      if (subId && subId !== '-1') {
        href += `/${subId}`
      }

      return href
    },

    closeAndNavigate(href) {
      this.isOpen = false
      this.searchText = ''
      this.searchResults = null

      // Navigate - use Vue Router
      if (this.$router && href !== this.$route.fullPath) {
        this.$router.push(href).catch(() => {
          // Ignore navigation errors (e.g., navigating to current route)
        })
      }
    },

    /**
     * Resolve selectedSearch from the current route.
     * Uses searchService.findOption() for detail pages,
     * or synthesizes a dashboard option for landing pages.
     */
    resolveSelectedSearch() {
      const { reportName, reportId, subId } = this.$route.params || {}
      const routeName = this.$route.name

      if (!routeName) return

      // Delivery views (pacing, discrepancy) use query params instead of path params
      const queryBillingCode = this.$route.query.billingCode
      const queryTeamId = this.$route.query.teamId

      if (queryBillingCode && ['pacing', 'discrepancy'].includes(reportName)) {
        const match = searchService.findOption({
          reportKey: reportName,
          reportId: queryBillingCode,
          subId: queryTeamId,
        })

        if (match) {
          this.$store.commit('tools/selectedSearch', match)
          return
        }

        // If no match in search index, don't overwrite selectedSearch —
        // the view's billingCode watcher handles this case
        return
      }

      // Detail/profile page — match against the search index
      if (reportId) {
        const match = searchService.findOption({
          reportKey: reportName,
          reportId,
          subId,
        })

        if (match) {
          this.$store.commit('tools/selectedSearch', match)
          return
        }

        // If no match in search index, don't overwrite selectedSearch —
        // the view component's data watcher handles setting it once data loads.
        // This prevents showing "Organizations" instead of client name on org profiles.
        return
      }

      // Dashboard/landing page — synthesize a dashboard option
      if (reportName) {
        const dashboardLabels = {
          pacing: 'Pacing Report',
          discrepancy: 'Discrepancy Report',
          'adx-health': 'AdX Health',
          'network-health': 'Network Health',
          'custom-reports': 'Custom Reports',
          'yield-partner': 'Yield',
          people: 'People',
          organizations: 'Organizations',
          projects: 'Projects',
          time: 'Time',
          dashboard: 'Dashboard',
          docs: 'Docs',
          admin: 'Admin',
          settings: 'Settings',
        }
        const label = dashboardLabels[reportName] || reportName
        this.$store.commit('tools/selectedSearch', {
          text: label,
          reportKey: reportName,
          reportName,
          route: routeName,
          dataType: 'custom',
          isSearchOption: true,
          reportId: '',
        })
        return
      }

      // Root dashboard (e.g., /dashboard)
      this.$store.commit('tools/selectedSearch', {
        text: 'Dashboard',
        reportKey: 'dashboard',
        reportName: 'dashboard',
        route: routeName,
        dataType: 'custom',
        isSearchOption: true,
        reportId: '',
      })
    },

    clearRecent() {
      searchService.clearRecentSearches()
      this.localRecentSearches = []
    },

    formatReportKey(key) {
      // Convert report key to readable format
      const formatMap = {
        pacing: 'Pacing',
        discrepancy: 'Discrepancy',
        'adx-health': 'AdX Health',
        'adx-and-ob': 'AdX & OB',
        'network-health': 'Network Health',
        'custom-reports': 'Custom Reports',
        'yield-partner': 'Yield',
        people: 'People',
        organizations: 'Org',
        projects: 'Project',
        time: 'Time',
        dashboard: 'Dashboard',
        docs: 'Docs',
        admin: 'Admin',
        settings: 'Settings',
      }
      return formatMap[key] || key
    },

    reportKeyColor(key) {
      const colorMap = {
        pacing: '#E3F2FD',
        discrepancy: '#FFF3E0',
        'adx-health': '#E8F5E9',
        'adx-and-ob': '#E8F5E9',
        'network-health': '#E8F5E9',
        'custom-reports': '#F3E5F5',
        organizations: '#E8EAF6',
        people: '#E8EAF6',
        projects: '#E0F2F1',
        time: '#FFF8E1',
        docs: '#ECEFF1',
        admin: '#FCE4EC',
        settings: '#ECEFF1',
      }
      return colorMap[key] || '#ECEFF1'
    },

    reportKeyTextColor(key) {
      const colorMap = {
        pacing: '#1565C0',
        discrepancy: '#E65100',
        'adx-health': '#2E7D32',
        'adx-and-ob': '#2E7D32',
        'network-health': '#2E7D32',
        'custom-reports': '#6A1B9A',
        organizations: '#283593',
        people: '#283593',
        projects: '#00695C',
        time: '#F57F17',
        docs: '#455A64',
        admin: '#C62828',
        settings: '#455A64',
      }
      return colorMap[key] || '#455A64'
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.search-form {
  width: 100%;
  max-width: 440px;
}

.search-input {
  ::v-deep .v-input__slot {
    background-color: rgba(0, 0, 0, 0.04) !important;
    border-radius: 8px;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.06) !important;
    }
  }

  ::v-deep .v-input__slot:focus-within {
    background-color: white !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.search-icon {
  color: $color-monochrome-coolgray;
  margin-right: 8px;
}

.search-dropdown-container {
  z-index: 200 !important;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.16), 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.search-dropdown {
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.search-empty-state {
  padding: 8px 0;
}

.search-results {
  padding: 4px 0;
}

.search-category {
  &:not(:last-child) {
    margin-bottom: 4px;
    padding-bottom: 4px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
}

.search-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: $color-monochrome-coolgray;
  text-transform: uppercase;
}

.search-result-item {
  min-height: 48px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  padding-top: 4px;
  padding-bottom: 4px;

  ::v-deep .v-list-item__icon {
    align-self: center;
    margin-top: 0;
    margin-bottom: 0;
  }

  ::v-deep .v-list-item__content {
    padding: 4px 0;
  }

  ::v-deep .v-list-item__subtitle {
    font-size: 11px;
    line-height: 1.3;
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ::v-deep .v-list-item__action {
    align-self: center;
    margin-top: 0;
    margin-bottom: 0;
    min-width: unset;
  }
}

.search-result-item:hover {
  background-color: rgba(33, 150, 243, 0.06);
}

.search-result-highlighted {
  background-color: rgba(33, 150, 243, 0.10);
}

.search-hint {
  border-top: 1px solid rgba(0, 0, 0, 0.06);

  kbd {
    display: inline-block;
    padding: 2px 6px;
    font-family: monospace;
    font-size: 11px;
    background-color: rgba(0, 0, 0, 0.06);
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }
}

.search-primer-alert {
  ::v-deep .v-alert__content {
    width: 100%;
  }
}

// Responsive adjustments
@media screen and (max-width: $vuetify-sm-max-size) {
  .search-form {
    max-width: 320px;
  }

  .search-dropdown {
    max-height: calc(100vh - 150px);
  }
}
</style>
